import type { CapabilityGateState } from "../capability-gate";
import type { OperationalPermission } from "./operational-permissions";
import {
  roleTemplatesForProfile,
  type OperationalRoleTemplate,
} from "./operational-role-templates";
import { operationalPreset } from "./business-operational-presets";
import type { ProductVertical } from "./operational-permissions";

export interface EffectiveOperationalContext {
  vertical: ProductVertical;
  businessProfile: string;
  roleId: string;
  planId: string | null;
  tenantPermissionOverrides?: Partial<Record<OperationalPermission, boolean>>;
  lockedCapabilities?: string[];
}

export interface ResolvedOperationalAccess {
  role: OperationalRoleTemplate;
  permissions: Set<OperationalPermission>;
  allowedModules: string[];
  hiddenModules: string[];
  dashboardWidgets: string[];
  quickActions: string[];
  navItems: Array<{ path: string; gateState: CapabilityGateState }>;
}

export function resolveEffectiveOperationalAccess(
  ctx: EffectiveOperationalContext,
): ResolvedOperationalAccess | null {
  const roles = roleTemplatesForProfile(ctx.vertical, ctx.businessProfile);
  const role = roles.find((r) => r.roleId === ctx.roleId)
    ?? roles.find((r) => r.roleId === "owner");
  if (!role) return null;

  const permissions = new Set<OperationalPermission>(role.permissions);
  if (ctx.tenantPermissionOverrides) {
    for (const [key, val] of Object.entries(ctx.tenantPermissionOverrides)) {
      const k = key as OperationalPermission;
      if (val === true) permissions.add(k);
      if (val === false) permissions.delete(k);
    }
  }

  const preset = operationalPreset(ctx.vertical, ctx.businessProfile);
  const allowedModules =
    role.allowedModules.includes("*")
      ? preset?.defaultNavigation.map((m) => `/app/${m}`) ?? []
      : [...role.allowedModules];

  const hidden = new Set(role.hiddenModules);
  for (const cap of ctx.lockedCapabilities ?? []) {
    if (cap === "gift_cards" || cap === "loyalty_points") hidden.add("/app/fidelizacion");
    if (cap === "portal_b2b") hidden.add("/app/produccion");
  }

  return {
    role,
    permissions,
    allowedModules,
    hiddenModules: [...hidden],
    dashboardWidgets: role.dashboardWidgets,
    quickActions: role.quickActions,
    navItems: allowedModules
      .filter((path) => !hidden.has(path))
      .map((path) => ({ path, gateState: "available" as CapabilityGateState })),
  };
}

export function hasOperationalPermission(
  access: ResolvedOperationalAccess | null,
  perm: OperationalPermission,
): boolean {
  return access?.permissions.has(perm) ?? false;
}
